// Routing for FluentValidation's keyed 400 responses back onto the step that owns
// each field (D-24094 §14).
//
// The API's validators live in Models/Jobs/*Request.cs and are registered globally,
// so a failed POST returns an ASP.NET ValidationProblemDetails whose `errors` map is
// keyed by the member chain of the rule that failed:
//
//   { "Step4.ShortDescription": ["..."], "Step2": ["'Step 2' must not be empty."] }
//
// Three key shapes have to be told apart:
//   "Step4.ShortDescription"  a field on a step  -> that step's own setError
//   "Step4"                   a step-level NotNull() -> that step, but no field to blame
//   "Guid" / anything else    not step-scoped   -> nothing can render it inline
//
// The step->index map is passed in per flow and MUST NOT be shared: only "Post a
// Job" numbers its keys StepN -> N-1. Reactivate's `Step2` DTO *is* PostJobStep7 and
// renders at UI index 1, and Prepaid Purchase spreads Step1/Step2 over three UI
// steps. Passing the wrong map would file errors on innocent steps, so each caller
// states its own mapping explicitly and lists only steps that can actually render a
// field error (i.e. have a react-hook-form instance).

/** Shown when a failure carries no `errors` map at all (a 500, a proxy error, …). */
export const GENERIC_SERVER_ERROR = 'We could not validate your listing. Please try again.';

export interface IParsedServerErrors {
  /** UI step index -> { react-hook-form field name: first message }. */
  fieldErrorsByStep: Record<number, Record<string, string>>;
  /** Lowest UI step index blamed by any key, field-level or step-level; null if none. */
  lowestStep: number | null;
  /** Messages no mounted field can show: step-level, unmapped steps, unprefixed keys. */
  unroutedMessages: string[];
  /** False when the body had no usable `errors` map — caller should fall back. */
  hasErrors: boolean;
}

/** ModelState values are string[]; tolerate a bare string just in case. */
const toMessages = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }
  return typeof value === 'string' && value ? [value] : [];
};

/** `ShortDescription` -> `shortDescription`. Every server member maps by first-char case. */
const toFieldName = (member: string): string => member.charAt(0).toLowerCase() + member.slice(1);

/**
 * Split a keyed validation response into per-step field errors, a navigation target,
 * and whatever could not be routed.
 *
 * `stepKeyToIndex` is matched case-insensitively, so both `Step2.JobTitle` and a
 * camel-cased `step2.jobTitle` land on the same step.
 */
export const parseServerStepErrors = (
  errors: unknown,
  stepKeyToIndex: Record<string, number>
): IParsedServerErrors => {
  const empty: IParsedServerErrors = {
    fieldErrorsByStep: {},
    lowestStep: null,
    unroutedMessages: [],
    hasErrors: false
  };

  // A non-validation failure (500, network, HTML error page) has no `errors` object.
  // Reading through it is what made the old Object.values(...) parsers throw.
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return empty;
  }

  const entries = Object.entries(errors as Record<string, unknown>);
  if (!entries.length) {
    return empty;
  }

  const lookup: Record<string, number> = {};
  Object.keys(stepKeyToIndex).forEach((key) => {
    lookup[key.toLowerCase()] = stepKeyToIndex[key];
  });

  const fieldErrorsByStep: Record<number, Record<string, string>> = {};
  const unroutedMessages: string[] = [];
  const blamedSteps: number[] = [];
  let hasErrors = false;

  entries.forEach(([key, value]) => {
    const messages = toMessages(value);
    if (!messages.length) {
      return;
    }
    hasErrors = true;

    const dot = key.indexOf('.');
    const prefix = dot === -1 ? key : key.slice(0, dot);
    const member = dot === -1 ? '' : key.slice(dot + 1);
    const stepIndex = lookup[prefix.toLowerCase()];

    // Unprefixed (Reactivate's bare `Guid`), or a step with no form to render into
    // (Step1/Step5 have no react-hook-form; Step7 is Stripe Elements).
    if (stepIndex === undefined) {
      unroutedMessages.push(...messages);
      return;
    }

    blamedSteps.push(stepIndex);

    // A dotless `StepN` is the step-level NotNull() — it blames the step, so we still
    // navigate there, but there is no field to attach the message to.
    // Nested members (`Step3.Locations[0].City`) have no matching control either.
    if (!member || member.indexOf('.') !== -1 || member.indexOf('[') !== -1) {
      unroutedMessages.push(...messages);
      return;
    }

    if (!fieldErrorsByStep[stepIndex]) {
      fieldErrorsByStep[stepIndex] = {};
    }
    fieldErrorsByStep[stepIndex][toFieldName(member)] = messages[0];
  });

  return {
    fieldErrorsByStep,
    lowestStep: blamedSteps.length ? Math.min(...blamedSteps) : null,
    unroutedMessages,
    hasErrors
  };
};

/** RTK Query surfaces the body under `error.data`; read `errors` defensively. */
export const getResponseErrors = (error: unknown): unknown => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const { data } = error as { data?: unknown };
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  return (data as { errors?: unknown }).errors;
};

/** Domain checkout failures use `{ error: string }` instead of ModelState `errors`. */
export const getResponseDomainError = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const { data } = error as { data?: unknown };
  if (!data || typeof data !== 'object') return undefined;
  const message = (data as { error?: unknown }).error;
  return typeof message === 'string' && message ? message : undefined;
};

/**
 * Flatten a keyed response to newline-joined text for flows that only show a
 * notification. Returns GENERIC_SERVER_ERROR when there is no `errors` map, which is
 * the case the old `Object.values(error.data.errors)` parsers crashed on.
 */
export const formatServerErrors = (error: unknown): string => {
  const errors = getResponseErrors(error);
  if (!errors || typeof errors !== 'object') {
    return GENERIC_SERVER_ERROR;
  }
  const text = Object.values(errors as Record<string, unknown>)
    .reduce<string[]>((acc, value) => acc.concat(toMessages(value)), [])
    .join('\n');
  return text || GENERIC_SERVER_ERROR;
};
